import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Flex,
  Text,
  Button,
  Select,
  Separator,
  IconButton,
} from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useGetAllBrands, useSaveKlaviyoMappings } from "hooks";

export function KlaviyoConfigSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();

  // Klaviyo-provided info (from location.state)
  const [klaviyoLists, setKlaviyoLists] = useState([]);

  // bconnect data
  const { data: brandsData, isLoading: isLoadingBrands } = useGetAllBrands(userProfile?.organisation_id);
  const { locationsData } = useLocationContext();

  const [bconnectBrands, setBconnectBrands] = useState([]); // non-default bconnect brands
  const [independentLocations, setIndependentLocations] = useState([]); // bconnect locations for "Locations" section

  // single mapping state keyed by bconnect_location_id -> { klaviyo_list_id, isMapped }
  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});

  // Per-brand temporary selection for "apply to brand"
  const [brandListSelections, setBrandListSelections] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [progressValue, setProgressValue] = useState(0);

  const saveKlaviyoMappings = useSaveKlaviyoMappings();

  // Normalize Klaviyo lists from location.state
  useEffect(() => {
    const raw = location.state?.lists;
    const listsFromState = (raw && raw.data) ? raw.data : (Array.isArray(raw) ? raw : []);
    setKlaviyoLists(
      (listsFromState || []).map((l) => ({
        id: String(l.id ?? ""),
        name: l.attributes?.name ?? l.name ?? "",
      }))
    );
  }, [location.state]);

  // Prepare bconnect lists (mirror setup logic)
  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = brandsData.find((b) => b.is_default);
      const nonDefault = brandsData.filter((b) => !b.is_default);
      setBconnectBrands(nonDefault || []);
      const defaultLocations = locationsData.filter((loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null);
      setIndependentLocations(defaultLocations || []);
    }
  }, [brandsData, locationsData]);

  // Build a map of brand -> its locations (prefer embedded brandsData[].locations)
  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) return map;

    bconnectBrands.forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
      }
    });

    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  // Also provide a flat list of all brand-owned locations
  const allBrandOwnedLocations = useMemo(() => Object.values(brandLocationsMap).flat(), [brandLocationsMap]);

  // If initial mappings available in location.state, use them. Otherwise start empty.
  useEffect(() => {
    const mappings = location.state?.mappings || null;
    if (!mappings) {
      setLocationMappings({});
      setOriginalLocationMappings({});
      return;
    }

    const lm = mappings.location_mappings || [];
    const locMap = {};
    const locOrig = {};

    lm.forEach((m) => {
      const key = String(m.bconnect_location_id ?? m.location_id ?? m.id ?? "");
      const entry = {
        klaviyo_list_id: m.klaviyo_list_id || "",
        // isMapped true if klaviyo_list_id present
        isMapped: Boolean(m.klaviyo_list_id),
      };
      locMap[key] = entry;
      locOrig[key] = { klaviyo_list_id: entry.klaviyo_list_id };
    });

    setLocationMappings(locMap);
    setOriginalLocationMappings(locOrig);
  }, [location.state]);

  // Handlers for per-location mapping (centralized)
  const handleLocationKlaviyoListChange = (bconnectLocationId, klaviyoListId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { klaviyo_list_id: "", isMapped: false };
      next[key] = { ...existing, klaviyo_list_id: klaviyoListId, isMapped: Boolean(klaviyoListId) };
      return next;
    });
  };

  // Handler: brand-level select change (temporary selection, not applied yet)
  const handleBrandListSelectionChange = (brandId, listId) => {
    setBrandListSelections((prev) => {
      const next = { ...(prev || {}) };
      next[String(brandId)] = listId;
      return next;
    });
  };

  // Handler: apply the selected brand list to all locations under the brand
  const handleApplyBrandList = (brandId) => {
    const selectedList = brandListSelections[String(brandId)];
    if (!selectedList) return;

    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      (brandLocs || []).forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = next[key] || { klaviyo_list_id: "", isMapped: false };
        next[key] = { ...existing, klaviyo_list_id: selectedList, isMapped: Boolean(selectedList) };
      });
      return next;
    });
  };

  // Validation & "dirty" detection for the brands (brand-owned locations) section
  const hasBrandSectionChanges = useMemo(() => {
    const brandLocations = allBrandOwnedLocations || [];

    // 1) No half-filled concept for Klaviyo (single-field) => proceed

    // 2) Must have at least one populated row that actually changed from original
    const hasAtLeastOneFullyPopulatedChange = brandLocations.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { klaviyo_list_id: "" };
      const currList = String(curr.klaviyo_list_id || "");
      const origList = String(orig.klaviyo_list_id || "");

      if (!currList) return false;
      return currList !== origList;
    });

    return Boolean(hasAtLeastOneFullyPopulatedChange);
  }, [allBrandOwnedLocations, locationMappings, originalLocationMappings]);

  // Validation & "dirty" detection for the independent (default) locations section
  const hasIndependentLocationChanges = useMemo(() => {
    const indep = independentLocations || [];

    const hasAtLeastOneFullyPopulatedChange = indep.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { klaviyo_list_id: "" };
      const currList = String(curr.klaviyo_list_id || "");
      const origList = String(orig.klaviyo_list_id || "");

      if (!currList) return false;
      return currList !== origList;
    });

    return Boolean(hasAtLeastOneFullyPopulatedChange);
  }, [independentLocations, locationMappings, originalLocationMappings]);

  // No half-filled checks for Klaviyo (single field), keep flags false
  const hasBrandHalfFilled = useMemo(() => false, [allBrandOwnedLocations, locationMappings]);
  const hasIndependentHalfFilled = useMemo(() => false, [independentLocations, locationMappings]);

  // Save handler which can save brand-owned or independent or both sections
  const handleSaveLocationMappings = async (section) => {
    try {
      setIsLoading(true);
      setProgressValue(10);

      let selectedLocations = [];
      if (section === "brands") {
        selectedLocations = allBrandOwnedLocations || [];
      } else if (section === "independent") {
        selectedLocations = independentLocations || [];
      } else {
        selectedLocations = [
          ...(independentLocations || []),
          ...(allBrandOwnedLocations || []),
        ];
      }

      const locationPayload = (selectedLocations || [])
        .map((loc) => {
          const locId = loc.id ?? loc.value ?? loc.location_id;
          const key = String(locId);
          const curr = locationMappings[key] || {};
          const currList = curr.klaviyo_list_id || null;
          const orig = originalLocationMappings[key] || {};
          const origList = orig.klaviyo_list_id || null;

          // require list to be present to save
          if (!currList) return null;

          // skip unchanged rows
          if (currList === origList) return null;

          const klaviyoListObj = klaviyoLists.find((kl) => String(kl.id) === String(currList));
          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            klaviyo_list_id: currList,
            klaviyo_list_name: klaviyoListObj?.name || "",
          };
        })
        .filter(Boolean);

      if (locationPayload.length === 0) {
        setToastMessage("No location changes to save");
        setIsLoading(false);
        setProgressValue(0);
        return;
      }

      setProgressValue(60);

      const payload = {
        organisation_id: userProfile.organisation_id,
        brand_mappings: [], // location-only save here
        location_mappings: locationPayload,
      };

      const result = await saveKlaviyoMappings.mutateAsync(payload);

      setProgressValue(95);

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      // Update originals for only the saved rows
      const newOrig = { ...originalLocationMappings };
      locationPayload.forEach((p) => {
        newOrig[String(p.bconnect_location_id)] = {
          klaviyo_list_id: p.klaviyo_list_id,
        };
      });
      setOriginalLocationMappings(newOrig);

      queryClient.invalidateQueries(["klaviyo-status"]);
      navigate("/activation-success", {
        state: {
          description: "Your customers will now sync with your selected Klaviyo lists.",
          integrationType: "klaviyo",
        },
      });
      setToastMessage("Location mappings saved successfully");
    } catch (err) {
      console.error("Error saving location mappings:", err);
      setToastMessage(err?.message || "Failed to save location mappings");
    } finally {
      setIsLoading(false);
      setProgressValue(0);
    }
  };

  if (!location.state) {
    return <div>No authentication data found. Please go back and log your credentials again.</div>;
  }

  return (
    <Flex style={{ minHeight: "100vh" }} justify="center" align="center" p="6">
      <Card style={{ maxWidth: "1000px", width: "100%" }} p="4">
        <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start">
          {/* Header */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" justify="between" align="center">
            <Flex display="flex" align="center" gap="4">
              <Flex
                style={{
                  borderRadius: "var(--space-1)",
                  background: "#FFFFFF",
                  border: "1px solid var(--gray-a6)",
                }}
                height="40px"
                width="40px"
                p="2"
                justify="center"
                align="center"
              >
                <img src={require("../../../assets/images/klaviyo.jpg")} alt="klaviyo" style={{ width: "24px" }} />
              </Flex>
              <Flex direction="column" justify="center" align="start">
                <Text size="4" weight="medium">Klaviyo</Text>
                <Text style={{ color: "var(--gray-a11)" }} size="1">
                  Map b connect locations to Klaviyo lists (map each location independently).
                </Text>
              </Flex>
            </Flex>

            <Flex display="flex" align="center" gap="4">
              <Button size="2" variant="soft" onClick={() => navigate(-1)} color="gray">Cancel</Button>
              <Button
                size="2"
                variant="soft"
                onClick={() => handleSaveLocationMappings("all")}
                loading={isLoading}
                disabled={
                  isLoading ||
                  hasBrandHalfFilled ||
                  hasIndependentHalfFilled ||
                  !(hasBrandSectionChanges || hasIndependentLocationChanges)
                }
              >
                Save and finish
              </Button>
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="4" />

          {/* BRANDS section: group bconnect locations under each bconnect brand */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5" mb="4">
            <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
              <Text size="2" weight="medium">Brands</Text>
              <Text size="1" weight="regular">Group: b connect brands with each of their locations (map each location independently).</Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              <Flex direction="row" justify="between" style={{ width: "100%" }}>
                <Text size="2" weight="medium" style={{ width: "48%" }}>b connect location</Text>
                <Text size="2" weight="medium" style={{ width: "48%" }}>Klaviyo list</Text>
              </Flex>

              {(bconnectBrands || []).length > 0 ? (
                (bconnectBrands || []).map((b) => {
                  const brandLocations = brandLocationsMap[b.id] || [];
                  return (
                    <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                      <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8 }}>
                        {/* make brand name flexible but truncating so controls stay visible */}
                        <Text
                          size="2"
                          weight="medium"
                          style={{
                            flex: "1 1 0%",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          [{b.name}]
                        </Text>

                        {/* Only show brand-level select + apply if brand has locations */}
                        {(brandLocationsMap[b.id] || []).length > 0 && (
                          <Flex
                            style={{
                              marginLeft: "8px",
                              gap: 8,
                              alignItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Select.Root
                              value={brandListSelections[String(b.id)] || ""}
                              onValueChange={(v) => handleBrandListSelectionChange(b.id, v)}
                              style={{ width: 220, minWidth: 120, flexShrink: 0 }}
                            >
                              <Select.Trigger
                                placeholder="Apply list to brand..."
                                style={{ width: "220px", boxSizing: "border-box" }}
                              >
                                <div
                                  style={{
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                  }}
                                  title={
                                    brandListSelections[String(b.id)]
                                      ? klaviyoLists.find(
                                          (kl) =>
                                            String(kl.id) ===
                                            String(brandListSelections[String(b.id)])
                                        )?.name || ""
                                      : "Apply list to brand..."
                                  }
                                >
                                  {brandListSelections[String(b.id)]
                                    ? klaviyoLists.find(
                                        (kl) =>
                                          String(kl.id) ===
                                          String(brandListSelections[String(b.id)])
                                      )?.name || ""
                                    : "Apply list to brand..."}
                                </div>
                              </Select.Trigger>
                              <Select.Content side="bottom" align="start" sideOffset={4}>
                                {klaviyoLists.map((kl) => (
                                  <Select.Item key={kl.id} value={kl.id} title={kl.name}>
                                    {kl.name}
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Root>

                            <Button
                              size="1"
                              variant="soft"
                              onClick={() => handleApplyBrandList(b.id)}
                              disabled={!brandListSelections[String(b.id)]}
                              style={{ flexShrink: 0 }}
                            >
                              Apply
                            </Button>
                          </Flex>
                        )}
                      </Flex>

                      {(brandLocations || []).length > 0 ? (
                        brandLocations.map((loc) => {
                          const locId = loc.id ?? loc.value ?? loc.location_id;
                          const key = String(locId);
                          const mapping = locationMappings[key] || {};

                          // ALWAYS show editable Select so user can change at will until Save
                          return (
                            <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                              <Flex style={{ width: "48%", minWidth: 0 }}>
                                <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                  <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                    {loc.name ?? loc.label ?? ""}
                                  </Text>
                                </Flex>
                              </Flex>

                              <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                                <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                  <ArrowRightIcon width="16" height="16" />
                                </IconButton>
                              </Flex>

                              <Flex style={{ width: "48%", minWidth: 0 }}>
                                <Select.Root value={mapping.klaviyo_list_id || ""} onValueChange={(v) => handleLocationKlaviyoListChange(key, v)} style={{ width: "100%" }}>
                                  <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.klaviyo_list_id ? (klaviyoLists.find((kl) => String(kl.id) === String(mapping.klaviyo_list_id))?.name || "") : "Select a Klaviyo list"}>
                                      {mapping.klaviyo_list_id ? (klaviyoLists.find((kl) => String(kl.id) === String(mapping.klaviyo_list_id))?.name || "") : "Select a Klaviyo list"}
                                    </div>
                                  </Select.Trigger>
                                  <Select.Content side="bottom" align="start" sideOffset={4}>
                                    {klaviyoLists.map((kl) => <Select.Item key={kl.id} value={kl.id} title={kl.name}>{kl.name}</Select.Item>)}
                                  </Select.Content>
                                </Select.Root>
                              </Flex>
                            </Flex>
                          );
                        })
                      ) : (
                        <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                          <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                        </Flex>
                      )}
                    </Flex>
                  );
                })
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No brands found.</Text>
                </Flex>
              )}
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="2" />

          {/* LOCATIONS (independent / default-brand locations) */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5">
            <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
              <Text size="2" weight="medium">Locations</Text>
              <Text size="1" weight="regular">Map independent b connect locations to a Klaviyo list.</Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              <Flex direction="row" justify="between" style={{ width: "100%" }}>
                <Text size="2" weight="medium" style={{ width: "48%" }}>b connect location</Text>
                <Text size="2" weight="medium" style={{ width: "48%" }}>Klaviyo list</Text>
              </Flex>

              {(independentLocations || []).length > 0 ? (
                (independentLocations || []).map((loc) => {
                  const locId = loc.id ?? loc.value ?? loc.location_id;
                  const key = String(locId);
                  const mapping = locationMappings[key] || {};
                  return (
                    <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                      <Flex style={{ width: "48%", minWidth: 0 }}>
                        <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                          <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                            {loc.name ?? loc.label ?? ""}
                          </Text>
                        </Flex>
                      </Flex>

                      <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>

                      <Flex style={{ width: "48%", minWidth: 0 }}>
                        <Select.Root value={mapping.klaviyo_list_id || ""} onValueChange={(v) => handleLocationKlaviyoListChange(key, v)} style={{ width: "100%" }}>
                          <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                            <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.klaviyo_list_id ? (klaviyoLists.find((kl) => String(kl.id) === String(mapping.klaviyo_list_id))?.name || "") : "Select a Klaviyo list"}>
                              {mapping.klaviyo_list_id ? (klaviyoLists.find((kl) => String(kl.id) === String(mapping.klaviyo_list_id))?.name || "") : "Select a Klaviyo list"}
                            </div>
                          </Select.Trigger>
                          <Select.Content side="bottom" align="start" sideOffset={4}>
                            {klaviyoLists.map((kl) => <Select.Item key={kl.id} value={kl.id} title={kl.name}>{kl.name}</Select.Item>)}
                          </Select.Content>
                        </Select.Root>
                      </Flex>
                    </Flex>
                  );
                })
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No locations found.</Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Flex>
  );
}
